/**
 * Whole-document undo/redo ownership for the editor.
 *
 * Armed while focus is inside the editor, a block is selected, or a recent
 * drag left focus on <body>. Cleared when focus moves into another app
 * control so those keep their own undo. Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y
 * cover structural changes native contenteditable undo can't restore.
 */

import { useEffect, useRef } from 'react';

export function useDocEditorUndo(editorRootRef, undo, redo) {
  const undoArmedRef = useRef(false);

  useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return undefined;
    const arm = () => {
      undoArmedRef.current = true;
    };
    const onFocusIn = (e) => {
      if (root.contains(e.target)) arm();
      else if (
        e.target &&
        (e.target.isContentEditable ||
          /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName))
      ) {
        undoArmedRef.current = false;
      }
    };
    root.addEventListener('pointerdown', arm);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      root.removeEventListener('pointerdown', arm);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [editorRootRef]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      const root = editorRootRef.current;
      const active = document.activeElement;
      const focusInside = active && root?.contains(active);
      const hasBlockSelection = !!root?.querySelector(
        '.multi-selected, .block-wrapper.selected'
      );
      // After a drag / image click, focus is often on <body> — still undo if
      // this editor is armed and nothing else has stolen focus into an input.
      const bodyFocus =
        !active || active === document.body || active === document.documentElement;
      // Toolbar is portaled outside .doc-editor; keep Ctrl+Z after Uncheck all.
      const focusInToolbar = !!active?.closest?.('.doc-editor-toolbar');
      // Image blocks blur the caret on select; treat a selected image as undoable
      // even when the wrapper class hasn't painted yet this frame.
      if (
        !root ||
        (!focusInside &&
          !hasBlockSelection &&
          !(undoArmedRef.current && bodyFocus) &&
          !(undoArmedRef.current && focusInToolbar))
      ) {
        return;
      }
      // Keep armed across undo of an image delete (body focus, no selection)
      undoArmedRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      if (isUndo) undo();
      else redo();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [editorRootRef, undo, redo]);

  return undoArmedRef;
}
